import type { BlogArticle, ExploreFeaturePage } from './types'

export const EXPLORE_FEATURES: ExploreFeaturePage[] = [
  {
    slug: 'escribir-novelas',
    path: '/explorar/escribir-novelas',
    navLabel: 'Escribir novelas',
    metaTitle: 'Escribir novelas online | Escribonauta',
    metaDescription:
      'Escribe tu novela online con guardado automático y modo concentración. Ninguna IA tiene acceso a tu historia: solo el escritor.',
    h1: 'Escribir novelas sin fricción',
    intro:
      'Escribonauta concentra el trabajo de redacción en un editor pensado para autores hispanohablantes: escribes, avanzas por capítulos y tienes la planificación a un clic. Tu manuscrito es solo tuyo: ninguna inteligencia artificial lo lee ni lo usa.',
    problemTitle: 'El problema que resuelve',
    problem:
      'Escribir una novela suele repartirse entre documentos sueltos, notas en el teléfono y hojas de cálculo. Ese caos corta el ritmo y hace más difícil terminar.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Escribir por capítulos con guardado automático',
      'Usar un editor compacto o un modo concentración a pantalla completa',
      'Consultar fichas, notas y tramas sin abandonar el texto',
      'Trabajar novelas, cuentos, guiones o poemarios según el tipo de proyecto',
      'Escribir con la tranquilidad de que ninguna IA tiene acceso a tu obra',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Creas un proyecto y eliges el tipo de obra.',
      'Escribes en el editor; cada capítulo queda organizado en tu manuscrito.',
      'Cuando necesitas contexto, abres el panel lateral o la planificación sin perder el foco.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Terminar un borrador de novela con sesiones diarias y conteo de palabras.',
      'Redactar un guion con formato de escena y diálogo.',
      'Alternar entre escritura libre y revisión por capítulos.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/personajes', label: 'Personajes' },
      { path: '/explorar/versiones', label: 'Versiones' },
      {
        path: '/blog/la-ia-no-lee-tu-historia',
        label: 'La IA no lee tu historia',
      },
    ],
  },
  {
    slug: 'organizar-novelas',
    path: '/explorar/organizar-novelas',
    navLabel: 'Organizar novelas',
    metaTitle: 'Organizar novelas y tramas | Escribonauta',
    metaDescription:
      'Organiza capítulos, arcos y eventos de tu novela en un panorama visual. Planifica la estructura antes o mientras escribes.',
    h1: 'Organizar la estructura de tu novela',
    intro:
      'La organización narrativa en Escribonauta te ayuda a ver el conjunto: capítulos, eventos y arcos en un mismo lugar para que la historia no se desarme.',
    problemTitle: 'El problema que resuelve',
    problem:
      'Cuando la trama crece, es fácil perder el hilo de qué ocurre en cada parte, qué falta por escribir y cómo se conectan los arcos.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Visualizar la estructura de la obra en un organizador de tramas',
      'Asociar notas y pendientes a capítulos o al proyecto completo',
      'Mantener la visión de conjunto mientras avanzas el manuscrito',
      'Combinar planificación y escritura en el mismo proyecto',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Abres la planificación de tu proyecto.',
      'Ordenas capítulos y eventos según la historia que quieres contar.',
      'Marcas pendientes y notas para no olvidar cabos sueltos.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Planificar los actos de una novela antes del primer borrador.',
      'Reordenar capítulos cuando descubres un mejor ritmo narrativo.',
      'Llevar el control de subtramas que se cruzan en distintos puntos.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/lineas-de-tiempo', label: 'Líneas de tiempo' },
      { path: '/explorar/analisis', label: 'Análisis' },
    ],
  },
  {
    slug: 'personajes',
    path: '/explorar/personajes',
    navLabel: 'Personajes',
    metaTitle: 'Personajes para novelas | Escribonauta',
    metaDescription:
      'Crea fichas de personajes con atributos, imágenes y relaciones. Mantén coherencia en tu elenco mientras escribes.',
    h1: 'Personajes con fichas claras',
    intro:
      'Las fichas narrativas de Escribonauta te permiten documentar personajes (y también lugares u objetos) con el detalle que tu historia necesita.',
    problemTitle: 'El problema que resuelve',
    problem:
      'Sin un lugar central para el elenco, aparecen inconsistencias: un color de ojos que cambia, una relación olvidada o un secundario que desaparece.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Crear fichas detalladas con atributos personalizables',
      'Añadir imágenes de referencia a cada personaje',
      'Ver relaciones entre personajes en un árbol visual',
      'Consultar fichas desde el editor mientras escribes',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Creas una ficha desde la planificación o el panel del editor.',
      'Completas rasgos, trasfondo y lo que sea relevante para la trama.',
      'Vinculas relaciones con otros elementos para no perder el mapa social de la historia.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Construir el elenco principal de una novela coral.',
      'Registrar secundarios que aparecen en pocos capítulos pero importan.',
      'Mantener coherencia visual y biográfica a lo largo de una saga.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/mapas', label: 'Mapas' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/lineas-de-tiempo', label: 'Líneas de tiempo' },
    ],
  },
  {
    slug: 'mapas',
    path: '/explorar/mapas',
    navLabel: 'Mapas',
    metaTitle: 'Mapas para escritores y novelas | Escribonauta',
    metaDescription:
      'Dibuja el mundo de tu historia: regiones, capas y marcas sobre una imagen base. Ideal para novelas de fantasía, viajes o ambientaciones complejas.',
    h1: 'Mapas para el mundo de tu novela',
    intro:
      'Con los mapas de Escribonauta puedes anclar lugares a una imagen, organizar capas y marcar puntos o recorridos que tu trama necesita.',
    problemTitle: 'El problema que resuelve',
    problem:
      'Si el escenario es amplio, las distancias, fronteras y rutas se vuelven confusas. Un mapa informal en papel se pierde; uno dentro del proyecto está siempre a mano.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Usar una imagen base y dibujar regiones sobre ella',
      'Organizar capas temáticas (política, relieve, rutas, etc.)',
      'Marcar puntos o líneas relevantes para la trama',
      'Vincular el espacio narrativo con el resto de la planificación',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Subes o eliges una imagen base para el mapa.',
      'Creas capas y dibujas las zonas que importan a la historia.',
      'Marcas lugares clave y los consultas cuando escribes escenas de viaje o conflicto territorial.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Diseñar el continente de una fantasía épica.',
      'Seguir la ruta de un personaje en una novela de viajes.',
      'Separar barrios o facciones en una ciudad ficticia.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/personajes', label: 'Personajes' },
      { path: '/explorar/lineas-de-tiempo', label: 'Líneas de tiempo' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
    ],
  },
  {
    slug: 'lineas-de-tiempo',
    path: '/explorar/lineas-de-tiempo',
    navLabel: 'Líneas de tiempo',
    metaTitle: 'Líneas de tiempo para novelas | Escribonauta',
    metaDescription:
      'Ordena eventos con cronologías interactivas y mantén la coherencia temporal de tu novela, saga o historia no lineal.',
    h1: 'Líneas de tiempo para tu historia',
    intro:
      'Las líneas de tiempo de Escribonauta ordenan lo que ocurre y cuándo ocurre, para que la cronología acompañe al manuscrito.',
    problemTitle: 'El problema que resuelve',
    problem:
      'En historias con saltos temporales, múltiples POVs o sagas largas, es fácil contradecir fechas, edades o el orden de los hechos.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Crear cronologías con eventos fechados',
      'Ver la secuencia narrativa de un vistazo',
      'Ajustar el orden cuando la trama cambia',
      'Coordinar tiempo narrativo con capítulos y personajes',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Creas una línea de tiempo en la planificación del proyecto.',
      'Añades eventos con la fecha o el marcador temporal que uses.',
      'La revisas al escribir para evitar contradicciones.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Alinear la infancia, el conflicto y el desenlace de un protagonista.',
      'Coordinar dos líneas temporales que se cruzan al final.',
      'Controlar el calendario interno de una saga de varios libros.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/personajes', label: 'Personajes' },
      { path: '/explorar/analisis', label: 'Análisis' },
    ],
  },
  {
    slug: 'analisis',
    path: '/explorar/analisis',
    navLabel: 'Análisis',
    metaTitle: 'Análisis de manuscrito | Escribonauta',
    metaDescription:
      'Revisa tu obra con herramientas de análisis para detectar patrones, revisar capítulos y mejorar el manuscrito con más claridad.',
    h1: 'Análisis para mejorar tu manuscrito',
    intro:
      'El análisis en Escribonauta te ayuda a mirar la obra con distancia: revisar capítulos, detectar qué necesita atención y orientar la reescritura.',
    problemTitle: 'El problema que resuelve',
    problem:
      'Después de cientos de páginas es difícil ver desequilibrios, repeticiones o capítulos que se quedan cortos sin una vista analítica.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Revisar el manuscrito con foco en capítulos y progreso',
      'Obtener una lectura más clara de cómo está quedando la obra',
      'Priorizar qué reescribir antes de compartir con lectores',
      'Complementar la intuición creativa con señales concretas',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Escribes o importas avance en tu proyecto.',
      'Abres las herramientas de análisis del manuscrito.',
      'Usas los hallazgos para planear la siguiente pasada de edición.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Preparar una revisión estructural antes de enviar a lectores beta.',
      'Detectar capítulos densos o demasiado breves.',
      'Orientar la reescritura de un segundo borrador.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/versiones', label: 'Versiones' },
    ],
  },
  {
    slug: 'versiones',
    path: '/explorar/versiones',
    navLabel: 'Versiones',
    metaTitle: 'Versiones y respaldos de tu novela | Escribonauta',
    metaDescription:
      'Versionamiento automático de tu novela, sin que ninguna IA lea tu obra. Restaura avances anteriores cuando lo necesites.',
    h1: 'Versiones de tu obra, siempre a mano',
    intro:
      'Escribonauta guarda respaldos de tu trabajo para que puedas experimentar con reescrituras sin miedo a perder un buen avance. Esas versiones son tuyas: ninguna inteligencia artificial las usa ni las analiza.',
    problemTitle: 'El problema que resuelve',
    problem:
      'Una reescritura arriesgada o un mal día pueden borrar semanas de progreso si no hay versiones anteriores fáciles de recuperar.',
    allowsTitle: 'Qué te permite hacer',
    allows: [
      'Contar con versionamiento automático ligado a tu flujo de trabajo',
      'Restaurar versiones anteriores cuando lo necesites',
      'Escribir con más libertad creativa al saber que hay respaldo',
      'Reducir la ansiedad de “romper” el manuscrito',
      'Mantener el historial de tu obra lejos del acceso de cualquier IA',
    ],
    howTitle: 'Cómo se utiliza',
    howSteps: [
      'Escribes con normalidad en tus sesiones.',
      'El sistema conserva versiones de tu obra a lo largo del proceso.',
      'Si una dirección no funciona, vuelves a un punto anterior.',
    ],
    examplesTitle: 'Ejemplos de uso',
    examples: [
      'Probar un final distinto sin perder el original.',
      'Recuperar un capítulo sobrescrito por error.',
      'Comparar el tono de un borrador previo con el actual.',
    ],
    relatedTitle: 'Sigue explorando',
    related: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/analisis', label: 'Análisis' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      {
        path: '/blog/la-ia-no-lee-tu-historia',
        label: 'La IA no lee tu historia',
      },
    ],
  },
]

export const BLOG_ARTICLES: BlogArticle[] = [
  {
    slug: 'la-ia-no-lee-tu-historia',
    path: '/blog/la-ia-no-lee-tu-historia',
    metaTitle: 'La IA no lee tu historia en Escribonauta | Blog',
    metaDescription:
      'En Escribonauta ninguna inteligencia artificial tiene acceso a tu obra. Solo el escritor puede leerla y editarla.',
    title: 'La IA no lee tu historia',
    publishedAt: '2026-09-21',
    readingMinutes: 4,
    summary:
      'Muchos autores dudan de subir su manuscrito a una plataforma online. Aquí dejamos claro qué ocurre en Escribonauta: tu historia es solo tuya.',
    sections: [
      {
        heading: 'La preocupación es legítima',
        paragraphs: [
          'Escribir implica exponer ideas, estilos y escenas que aún no quieres compartir. Con el auge de la inteligencia artificial, es natural preguntarse si una herramienta online podría usar tu texto para entrenar modelos o “leer” tu obra sin tu permiso.',
          'Esa duda no debería frenar tu proceso creativo. Mereces un lugar donde escribir con foco y sin esa inquietud de fondo.',
        ],
      },
      {
        heading: 'Qué garantiza Escribonauta',
        paragraphs: [
          'Ninguna inteligencia artificial tiene acceso a tu historia en Escribonauta. Solo el escritor puede leerla y editarla.',
          'La plataforma está hecha para organizar, escribir y gestionar tu proyecto literario: capítulos, planificación, versiones y herramientas de apoyo. No para que un modelo de IA consuma tu manuscrito.',
        ],
      },
      {
        heading: 'Escribe con tranquilidad',
        paragraphs: [
          'Puedes avanzar tu novela, guardar versiones y planificar tramas sabiendo que el control de la obra sigue en tus manos.',
          'Si buscas un editor pensado para autores hispanohablantes, sin ceder tu texto a una IA, Escribonauta está diseñado para eso.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/versiones', label: 'Versiones' },
    ],
  },
  {
    slug: 'como-organizar-una-novela',
    path: '/blog/como-organizar-una-novela',
    metaTitle: 'Cómo organizar una novela | Blog Escribonauta',
    metaDescription:
      'Una guía práctica para organizar capítulos, tramas y pendientes de tu novela sin perder el rumbo creativo.',
    title: 'Cómo organizar una novela',
    publishedAt: '2026-09-01',
    readingMinutes: 6,
    summary:
      'Organizar una novela no es rigidizar la creatividad: es darle a la historia un mapa para que puedas terminarla.',
    sections: [
      {
        heading: 'Empieza por el panorama, no por el detalle',
        paragraphs: [
          'Antes de pulir frases, conviene saber qué historia estás contando y en cuántas partes grandes se divide. Una lista de actos o de grandes movimientos narrativos basta para orientarte.',
          'Cuando el panorama está claro, los capítulos dejan de ser carpetas sueltas y pasan a ser peldaños hacia un final.',
        ],
      },
      {
        heading: 'Separa estructura, ideas y tareas',
        paragraphs: [
          'Mezclar el esquema de la trama con notas sueltas y recordatorios suele generar ruido. Mantén la estructura en un organizador, las ideas en notas y lo operativo en pendientes.',
          'Así, cuando te sientes a escribir, sabes si toca avanzar texto, resolver un cabo suelto o reconsiderar un arco.',
        ],
      },
      {
        heading: 'Revisa la organización a medida que escribes',
        paragraphs: [
          'La novela real siempre corrige el plan. Reserva momentos para actualizar el esquema cuando descubras un mejor orden de escenas o un giro más potente.',
          'En Escribonauta puedes combinar el organizador de tramas con la escritura en el mismo proyecto, para que el mapa y el manuscrito evolucionen juntos.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
    ],
  },
  {
    slug: 'como-crear-personajes-para-una-novela',
    path: '/blog/como-crear-personajes-para-una-novela',
    metaTitle: 'Cómo crear personajes para una novela | Blog Escribonauta',
    metaDescription:
      'Consejos para crear personajes memorables: deseo, conflicto, relaciones y fichas que sostienen la coherencia de tu novela.',
    title: 'Cómo crear personajes para una novela',
    publishedAt: '2026-09-01',
    readingMinutes: 7,
    summary:
      'Un personaje memorable quiere algo, choca con obstáculos y deja huella en los demás. La ficha es el soporte; la escena es la prueba.',
    sections: [
      {
        heading: 'Define deseo y conflicto',
        paragraphs: [
          'Pregunta qué quiere cada personaje relevante y qué se lo impide. Sin deseo no hay motor; sin obstáculo no hay drama.',
          'No hace falta una biografía interminable: hace falta claridad sobre qué lo mueve en esta historia.',
        ],
      },
      {
        heading: 'Construye relaciones, no fichas aisladas',
        paragraphs: [
          'Los personajes brillan en relación con otros: alianzas, deudas, secretos compartidos, rivalidades. Un mapa de vínculos evita elencos planos.',
          'Documentar esas relaciones te ayuda a escribir diálogos con tensión real y a no olvidar conexiones importantes.',
        ],
      },
      {
        heading: 'Usa fichas como brújula, no como prisión',
        paragraphs: [
          'Una ficha sirve para no contradecirte y para recuperar detalles cuando vuelves a un personaje semanas después.',
          'En Escribonauta puedes crear fichas con atributos e imágenes, y consultarlas mientras escribes para mantener coherencia sin salir del flujo.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/personajes', label: 'Personajes' },
      { path: '/explorar/lineas-de-tiempo', label: 'Líneas de tiempo' },
    ],
  },
  {
    slug: 'como-crear-una-linea-de-tiempo-para-una-novela',
    path: '/blog/como-crear-una-linea-de-tiempo-para-una-novela',
    metaTitle: 'Cómo crear una línea de tiempo para una novela | Blog Escribonauta',
    metaDescription:
      'Aprende a construir una línea de tiempo narrativa para mantener fechas, edades y orden de eventos bajo control.',
    title: 'Cómo crear una línea de tiempo para una novela',
    publishedAt: '2026-09-01',
    readingMinutes: 6,
    summary:
      'Una línea de tiempo no sustituye la prosa: sostiene la coherencia cuando la historia juega con el orden de los hechos.',
    sections: [
      {
        heading: 'Elige la unidad temporal que sí usas',
        paragraphs: [
          'No todas las novelas necesitan calendario gregoriano. A veces bastan “día 1 / día 12”, estaciones o edades. Usa la unidad que tu trama realmente consulta.',
          'Lo importante es que puedas responder: ¿esto ocurre antes o después de aquello?',
        ],
      },
      {
        heading: 'Marca hitos, no cada minuto',
        paragraphs: [
          'Empieza por eventos que cambian la historia: partidas, revelaciones, confrontaciones, muertes, alianzas. Luego rellena huecos solo donde haya riesgo de contradicción.',
          'Una cronología sobrecargada se abandona; una útil se consulta.',
        ],
      },
      {
        heading: 'Conéctala con capítulos y personajes',
        paragraphs: [
          'La línea de tiempo gana valor cuando la contrastas con lo que ya escribiste y con la edad o trayectoria de tus personajes.',
          'En Escribonauta puedes crear cronologías interactivas dentro del proyecto y enlazar ese orden temporal con el resto de la planificación.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/lineas-de-tiempo', label: 'Líneas de tiempo' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
    ],
  },
  {
    slug: 'como-planificar-una-novela',
    path: '/blog/como-planificar-una-novela',
    metaTitle: 'Cómo planificar una novela | Blog Escribonauta',
    metaDescription:
      'Método sencillo para planificar una novela: premisa, estructura, personajes y ritmo de escritura sin frenar la creatividad.',
    title: 'Cómo planificar una novela',
    publishedAt: '2026-09-01',
    readingMinutes: 8,
    summary:
      'Planificar es decidir lo suficiente para escribir con dirección, dejando espacio para lo que solo aparece al redactar.',
    sections: [
      {
        heading: 'Aterriza la premisa en una frase',
        paragraphs: [
          'Si no puedes decir de qué trata la novela en una o dos frases claras, el plan se dispersará. La premisa es el norte del resto de decisiones.',
          'Incluye protagonista, deseo central y el tipo de conflicto que lo complica.',
        ],
      },
      {
        heading: 'Arma un esqueleto flexible',
        paragraphs: [
          'Define inicio, punto de no retorno, crisis y cierre. No necesitas cada escena el día uno; sí necesitas saber hacia dónde camina la tensión.',
          'Ese esqueleto te permite escribir capítulos con propósito aunque aún no conozcas todos los detalles.',
        ],
      },
      {
        heading: 'Planifica el hábito, no solo la trama',
        paragraphs: [
          'Una novela se termina con constancia. Decide un ritmo realista de sesiones y un lugar donde vivir el proyecto completo: texto, plan y notas.',
          'Escribonauta está pensado para ese flujo: escribir, organizar y revisar en un solo lugar, para que el plan acompañe al manuscrito hasta el final.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/personajes', label: 'Personajes' },
    ],
  },
  {
    slug: 'escribir-sin-ia',
    path: '/blog/escribir-sin-ia',
    metaTitle: 'Escribir tu novela sin que la IA lea tu historia | Blog Escribonauta',
    metaDescription:
      'Cómo escribir y planificar tu novela sin que ninguna inteligencia artificial tenga acceso a tu manuscrito. Herramientas y flujo de trabajo para autores.',
    title: 'Escribir tu novela sin que la IA lea tu historia',
    publishedAt: '2026-10-03',
    readingMinutes: 6,
    summary:
      'Escribir en digital ya no significa ceder tu manuscrito a una IA. Aquí te mostramos cómo organizar, redactar y compartir tu novela manteniendo el control total de tu obra.',
    sections: [
      {
        heading: 'El dilema del escritor digital',
        paragraphs: [
          'Cada vez más autores trabajan en plataformas online por la comodidad de escribir desde cualquier lugar, tener respaldos automáticos y acceder a herramientas de planificación integradas. Pero muchos dudan antes de subir su manuscrito: ¿quién tiene acceso a mi texto? ¿Lo usarán para entrenar una inteligencia artificial?',
          'Esa preocupación es legítima. Tu novela es tu trabajo creativo, tu voz, tu propiedad intelectual. Mereces escribir en digital sin renunciar al control de tu obra.',
        ],
      },
      {
        heading: 'Qué necesitas para escribir sin IA en el medio',
        paragraphs: [
          'Un editor que guarde tu texto sin procesarlo con modelos de lenguaje. Un organizador de tramas que te ayude a estructurar capítulos, arcos y personajes sin que esos datos alimenten algoritmos. Un sistema de versiones que respalde tu avance sin enviarlo a servidores que entrenen IA.',
          'Y cuando llegue el momento de recibir feedback, un modo de compartir la obra con lectores beta sin exponer el manuscrito a terceros no autorizados.',
        ],
      },
      {
        heading: 'El flujo de trabajo sin IA',
        paragraphs: [
          'Primero, escribes en un editor diseñado para sesiones largas, con guardado automático y sin distracciones. Tu texto queda almacenado de forma privada.',
          'Luego, organizas la trama: capítulos, eventos, personajes, cronologías. Todo en el mismo proyecto, sin salir de la plataforma, pero sin que ningún modelo de IA procese esa información.',
          'Cuando terminas un borrador o una versión lista para feedback, generas un enlace privado o un código de acceso. Tus lectores beta comentan capítulo a capítulo, pero el manuscrito no se comparte públicamente ni se usa para entrenar modelos.',
        ],
      },
      {
        heading: 'Escribir con tranquilidad',
        paragraphs: [
          'Escribir sin IA leyendo tu historia no significa renunciar a las ventajas de lo digital. Significa elegir herramientas que respeten la privacidad de tu obra desde el diseño.',
          'En Escribonauta puedes escribir, planificar y compartir tu novela sabiendo que tu manuscrito es solo tuyo. Editor, organizador de tramas, fichas de personajes, versionamiento y lectores beta: todo sin que una inteligencia artificial tenga acceso a tu texto.',
          'Si buscas terminar tu novela con la tranquilidad de que tu historia sigue siendo privada, esta es la herramienta que estabas buscando.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/blog/la-ia-no-lee-tu-historia', label: 'La IA no lee tu historia' },
    ],
  },
  {
    slug: 'planificar-capitulos-y-escenas-en-un-fin-de-semana',
    path: '/blog/planificar-capitulos-y-escenas-en-un-fin-de-semana',
    metaTitle: 'Planificar capítulos y escenas en un fin de semana | Blog Escribonauta',
    metaDescription:
      'Guía práctica para planificar capítulos y escenas clave en un fin de semana, antes de escribir el primer borrador de tu novela.',
    title: 'Planificar capítulos y escenas en un fin de semana',
    publishedAt: '2026-10-10',
    readingMinutes: 7,
    summary:
      'Si ya tienes premisa y personajes pero no sabes por dónde empezar a escribir, un fin de semana concentrado puede convertir la idea en un mapa de capítulos y escenas listo para el borrador.',
    sections: [
      {
        heading: 'Planificar no es posponer la escritura',
        paragraphs: [
          'Muchos autores confunden planificar con quedarse eternamente en la fase de “investigación” o de notas sueltas. El objetivo de un plan de capítulos y escenas no es sustituir la prosa: es reducir la fricción del primer párrafo y evitar que la historia se desordene a mitad de camino.',
          'Un fin de semana bien usado basta para pasar de “sé de qué va la novela” a “sé qué ocurre en cada parte y qué escena abro mañana”. No necesitas el guion completo ni cada diálogo; necesitas decisiones suficientes para escribir con dirección y para reconocer cuando una escena nueva merece un capítulo propio.',
          'Si ya leíste guías generales sobre premisa y estructura, este método asume ese trabajo hecho y se enfoca en bajar un nivel: de la novela entera a capítulos con función y escenas que mueven la trama.',
        ],
      },
      {
        heading: 'Viernes: fija el norte en una página',
        paragraphs: [
          'Reserva dos o tres horas. Escribe la premisa en dos frases: quién lleva la historia, qué quiere y qué se lo complica de forma concreta. Si no cabe en dos frases, aún no está lo bastante clara para repartir capítulos.',
          'Añade tres decisiones rápidas: tono aproximado (íntimo, épico, irónico), tipo de final que imaginas (cerrado, abierto, ambiguo) y punto de vista principal. No son contratos eternos; son brújula para no discutir contigo mismo cada vez que abres el documento.',
          'Cierra el viernes con una lista corta de personajes que aparecerán en el primer tercio de la obra. Anota en una línea qué aporta cada uno al conflicto. Eso evita capítulos de relleno solo para “presentar” a alguien que luego desaparece.',
        ],
      },
      {
        heading: 'Sábado: arma la escalera de capítulos',
        paragraphs: [
          'Divide la novela en tres bloques grandes: inicio, desarrollo y desenlace. No hace falta teoría literaria: piensa en dónde el lector entiende el problema, dónde la situación empeora y dónde se resuelve o se transforma.',
          'Crea una tabla o lista numerada. Por cada capítulo, anota un título provisional y una sola línea que responda: “¿Qué cambia al terminar este capítulo?”. Debe cambiar algo: una relación, un secreto revelado, un riesgo nuevo, una decisión irreversible.',
          'Marca con un símbolo los capítulos que ya visualizas con claridad y deja los demás como huecos conscientes. Los huecos no son fallos; son espacio para lo que solo descubrirás al escribir. Lo importante es que la escalera tenga peldaños, no que cada peldaño esté pulido.',
        ],
      },
      {
        heading: 'Domingo: baja a escenas que empujan la historia',
        paragraphs: [
          'Elige los capítulos más críticos: los tres primeros, el punto medio aproximado y los dos o tres finales. En cada uno, anota de dos a cuatro escenas en orden. Por escena, escribe cuatro datos breves: lugar, personajes presentes, conflicto de la escena y resultado (qué queda distinto al salir).',
          'Si una escena no tiene conflicto claro, probablemente sea transición: útil en la prosa, pero no debe ocupar el lugar de un capítulo entero en el plan. Las escenas que más te da pereza planificar suelen ser las que más necesitan estar en el mapa.',
          'Para el tramo central de la novela, basta con una línea por capítulo hasta que llegues a escribir esa zona. El plan del domingo debe dejarte listo para abrir el capítulo 1 el lunes, no para cerrar el manuscrito en papel.',
        ],
      },
      {
        heading: 'Lunes y después: escribe y ajusta el mapa',
        paragraphs: [
          'Empieza por la primera escena del capítulo 1, no por reordenar el plan. Escribe una sesión completa antes de volver a tocar la estructura. La prosa siempre devuelve información que el esquema no tenía: un personaje secundario que pide más espacio, un giro que encaja mejor dos capítulos antes o un ritmo que pide capítulos más cortos de lo previsto.',
          'Reserva media hora semanal para actualizar la lista de capítulos y escenas. Borra lo que sobra, divide capítulos que crecieron demasiado y anota pendientes concretos (“explicar por qué X confía en Y”) en lugar de notas vagas.',
          'Para mantener capítulos, trama y personajes en un mismo proyecto sin tarjeta de crédito, puedes usar Escribonauta: organizas el mapa ahí y escribes sabiendo que tu historia es privada y que ninguna inteligencia artificial lee tu manuscrito. El plan y el borrador evolucionan juntos hasta que la novela encuentre su forma definitiva.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/personajes', label: 'Personajes' },
    ],
  },
  {
    slug: 'como-compartir-tu-novela-con-lectores-beta',
    path: '/blog/como-compartir-tu-novela-con-lectores-beta',
    metaTitle: 'Cómo compartir tu novela con lectores beta | Blog Escribonauta',
    metaDescription:
      'Cuándo buscar lectores beta, cómo elegirlos, qué pedirles y cómo compartir tu manuscrito en privado sin que una IA lea tu historia.',
    title: 'Cómo compartir tu novela con lectores beta',
    publishedAt: '2026-10-16',
    readingMinutes: 7,
    summary:
      'Los lectores beta pueden ayudarte a ver huecos y ritmo antes de publicar, pero compartir un borrador exige confianza. Aquí tienes pasos concretos para elegir lectores, pedir feedback útil y enviar el manuscrito solo a quien tú decidas.',
    sections: [
      {
        heading: 'Para qué sirven los lectores beta (y cuándo conviene)',
        paragraphs: [
          'Un lector beta no es un editor profesional ni un crítico literario: es alguien que lee tu borrador con honestidad y te devuelve impresiones antes de que la obra salga al mundo. Su valor está en lo que tú no puedes ver desde dentro: si un capítulo se hace largo, si un personaje convence, si el final cierra emocionalmente.',
          'Conviene buscar betas cuando ya tienes un borrador completo o al menos un tramo sustancial (por ejemplo, el primer acto o los capítulos centrales). Antes de eso, suele ser más útil reescribir a solas o con notas de planificación. Si cada párrafo aún cambia de color, el feedback se vuelve ruido.',
          'Tampoco hace falta esperar a la “versión perfecta”. El objetivo es una lectura que aún puedas modificar: suficientemente pulida para que el lector siga la historia, pero abierta a cambios estructurales si hace falta.',
        ],
      },
      {
        heading: 'Elige a pocas personas y con criterio',
        paragraphs: [
          'Menos lectores, mejor señal. Tres o cinco personas que lean con atención valen más que veinte que solo devuelven “me gustó”. Busca perfiles distintos: alguien que lea mucho tu género, alguien que no lo lea tanto (para detectar si la historia engancha sin conocimiento previo) y, si puedes, una persona que te diga las cosas sin endulzar.',
          'Evita mezclar en la misma ronda a quien no puede separar la obra de tu relación personal. Un familiar muy cercano puede ser sincero, pero a veces cuesta distinguir si critica el texto o te protege. Tampoco conviene enviar el manuscrito a quien pueda filtrarlo sin tu permiso.',
          'Antes de mandar nada, acuerda plazos realistas y el formato del feedback: comentarios por capítulo, un correo al final, una llamada de media hora. Así nadie se siente obligado a escribir una reseña larga si solo tiene tiempo para puntos clave.',
        ],
      },
      {
        heading: 'Qué pedirles (y qué dejar fuera)',
        paragraphs: [
          'Las preguntas abiertas suelen dar respuestas vagas. Mejor una lista corta y repetible: ¿En qué momento perdiste interés o te confundiste? ¿Qué personaje te importó más y por qué? ¿Hubo algo que no te creyó? ¿El final te satisfizo?',
          'Si te preocupa el ritmo, pide que marquen el capítulo donde casi dejaron de leer. Si te preocupa la voz narrativa, pide que citen dos frases que les sonaron “a ti” y dos que les sonaron raras. Eso orienta la reescritura sin que el lector haga de coautor.',
          'No pidas que corrijan cada coma ni que reescriban escenas: eso desvía el foco. Y deja claro que puedes ignorar sugerencias: el manuscrito sigue siendo tuyo. El beta lee; tú decides qué entra en la siguiente versión.',
        ],
      },
      {
        heading: 'Recibe críticas sin perderte la voz',
        paragraphs: [
          'Cuando llegan los comentarios, léelos todos una vez sin tocar el texto. Anota patrones: si tres personas se pierden en el mismo capítulo, probablemente hay un problema real ahí. Si solo una persona odia un giro que tú amas, quizá sea preferencia, no error.',
          'Separa feedback de estilo y de estructura. “No me gustó el tono” puede significar que ese lector no es tu público ideal. “No entendí por qué el protagonista hizo X” casi siempre es una señal de claridad narrativa que merece revisión.',
          'Después de procesar, elige un puñado de cambios concretos para la siguiente versión. Actualiza el borrador, guarda esa versión con un nombre claro y, si haces una segunda ronda de betas, comparte solo lo que cambió en los tramos críticos. Así no agotas a quien ya te ayudó.',
        ],
      },
      {
        heading: 'Comparte en privado: solo las personas que tú eliges',
        paragraphs: [
          'Enviar un Word por correo o subir el PDF a un enlace público son opciones habituales, pero pierdes control: copias sueltas, enlaces reenviados, carpetas que no sabes quién abrió. Para una novela en construcción, conviene un canal donde tú decidas quién entra y puedas cerrar el acceso cuando termine la ronda.',
          'Elegir dónde compartir también es elegir quién puede “ver” el texto en el sentido digital. Muchos escritores hoy evitan plataformas que procesan el manuscrito con inteligencia artificial. Tu historia no debería usarse para entrenar modelos ni quedar expuesta a lectores que no invitaste.',
          'En Escribonauta puedes invitar lectores beta con acceso privado al proyecto: ellos comentan capítulo a capítulo y tú mantienes el manuscrito bajo tu control, sin que una IA lea tu obra. El plan actual es gratuito y no pide tarjeta de crédito, así que puedes probar el flujo completo —escribir, organizar y compartir— cuando sientas que tu borrador está listo para ojos ajenos.',
          'Empieza con una lista corta de preguntas, unas pocas personas de confianza y un espacio donde la novela siga siendo solo tuya hasta que tú decidas lo contrario. Ese es el punto de los lectores beta: ver mejor la historia sin dejar de ser su autora o autor.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/versiones', label: 'Versiones' },
      { path: '/blog/escribir-sin-ia', label: 'Escribir sin IA' },
    ],
  },
  {
    slug: 'constancia-para-terminar-tu-novela',
    path: '/blog/constancia-para-terminar-tu-novela',
    metaTitle: 'Constancia para terminar tu novela | Blog Escribonauta',
    metaDescription:
      'Cómo crear un hábito de escritura realista, superar los bloqueos y llegar al final de tu novela sin depender solo de la inspiración.',
    title: 'Constancia para terminar tu novela',
    publishedAt: '2026-10-23',
    readingMinutes: 7,
    summary:
      'Terminar una novela depende menos de un golpe de inspiración y más de volver al texto con regularidad. Aquí tienes un enfoque práctico para sostener el ritmo sin quemarte.',
    sections: [
      {
        heading: 'La novela se termina en la repetición, no en el brillo',
        paragraphs: [
          'Muchos proyectos se abandonan no por falta de talento, sino porque la vida se interpone: trabajo, cansancio, dudas sobre si la historia “vale la pena”. La inspiración abre la puerta; la constancia es la que te deja dentro de la habitación el mes siguiente.',
          'Constancia no significa escribir ocho horas al día ni sentir entusiasmo cada sesión. Significa acordar un ritmo que puedas cumplir la mayoría de las semanas y protegerlo como protegerías una cita importante. Un avance modesto pero constante supera a las ráfagas heroicas seguidas de semanas de silencio.',
          'Si ya tienes premisa, personajes o un mapa de capítulos, el siguiente paso decisivo suele ser el hábito: decidir cuándo escribes, cuánto tiempo y qué cuenta como “haber trabajado” ese día.',
        ],
      },
      {
        heading: 'Elige un objetivo tan pequeño que sea difícil fallar',
        paragraphs: [
          '“Escribir la novela” es demasiado grande para el cerebro diario. Mejor: “25 minutos en el capítulo 4” o “400 palabras antes de cenar”. El número concreto depende de tu vida; lo importante es que sea alcanzable incluso en un mal día.',
          'Algunos autores miden por tiempo, otros por palabras, otros por “una escena”. Prueba una semana con cada método y quédate con el que menos fricción te genere al abrir el editor. Si terminas la sesión con ganas de seguir, el objetivo quizá pueda subir un poco; si cada día lo pospones, bájalo sin culpa.',
          'Registra lo mínimo: fecha y si escribiste o no. Un calendario con marcas visibles ayuda más que una lista mental de buenas intenciones. Ver diez días seguidos con una X cambia la conversación interna de “no soy constante” a “ya llevo diez”.',
        ],
      },
      {
        heading: 'Reduce la fricción antes de sentarte a escribir',
        paragraphs: [
          'Cada minuto que pierdes buscando el archivo correcto, el capítulo del mes pasado o las notas en otra app es un minuto menos de prosa. Centralizar manuscrito, plan y fichas en un solo proyecto evita que “preparar la sesión” se convierta en excusa para no empezar.',
          'Deja listo el punto de entrada: al cerrar una sesión, anota en una línea la siguiente escena o el siguiente párrafo que toca. Al volver, no decides desde cero; retomas un hilo. Ese puente entre sesiones es uno de los trucos más baratos para mantener el ritmo.',
          'El lugar y el momento también importan. No hace falta un estudio perfecto: una misma silla, la misma hora y el teléfono en otra habitación bastan para que el cerebro asocie “ahora toca escribir”. La constancia se apoya en señales repetidas, no solo en voluntad.',
        ],
      },
      {
        heading: 'Qué hacer cuando no tienes ganas (o cuando el bloqueo aparece)',
        paragraphs: [
          'Habrá días en que el texto se sienta plano. La regla útil no es “escribir bien”, sino “abrir el proyecto”. Si tras quince minutos sigues atascado, cambia de tarea dentro del mismo universo: actualizar una ficha, ordenar un capítulo en el organizador, anotar un pendiente concreto para mañana. Eso cuenta como mantener el contrato contigo mismo.',
          'El bloqueo narrativo a veces es señal de un cabo suelto en la trama, no de falta de disciplina. Antes de abandonar el capítulo, pregúntate si sabes qué debe cambiar al final de la escena. Si no lo sabes, planifica cinco minutos; si lo sabes y no fluye, escribe mal a propósito y corrige después.',
          'También conviene planificar pausas conscientes: un fin de semana sin escribir no arruina un hábito si vuelves el lunes con la misma regla de siempre. Lo que erosiona la constancia es la culpa infinita o renegociar cada semana “desde cero” sin aprender qué ritmo sí funciona.',
        ],
      },
      {
        heading: 'Mira el progreso en capítulos, no solo en páginas sueltas',
        paragraphs: [
          'Contar palabras totales motiva al inicio; a mitad de novela puede abrumar. Alterna con metas por capítulo: “este mes cierro el 7” o “termino el primer acto”. Los hitos dan sensación de cierre y evitan la sensación de escribir en el vacío durante meses.',
          'Cuando un capítulo queda en borrador, márcalo como avance real aunque aún no esté pulido. Terminar la novela es llegar al final del borrador completo; la edición fina viene después. Mezclar reescritura obsesiva en el capítulo 2 con el capítulo 30 sin escribir suele frenar más que ayudar.',
          'Si quieres un lugar gratuito para escribir, organizar capítulos y ver el manuscrito crecer sin que una inteligencia artificial lea tu historia, puedes empezar en Escribonauta (https://escribonauta.cl): no requiere tarjeta y está pensado para que el texto, la planificación y las versiones vivan juntos mientras avanzas sesión a sesión.',
          'La constancia no te garantiza un bestseller; te garantiza algo más valioso para la mayoría de los autores: un borrador terminado que puedas revisar, compartir con lectores beta o guardar como la primera versión real de tu novela. Elige un ritmo pequeño, protégelo y vuelve mañana.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/organizar-novelas', label: 'Organizar novelas' },
      { path: '/blog/como-planificar-una-novela', label: 'Cómo planificar una novela' },
    ],
  },
  {
    slug: 'como-cerrar-el-primer-borrador-de-tu-novela',
    path: '/blog/como-cerrar-el-primer-borrador-de-tu-novela',
    metaTitle: 'Cómo cerrar el primer borrador de tu novela | Blog Escribonauta',
    metaDescription:
      'Guía para terminar la primera vuelta de tu novela: qué cuenta como borrador cerrado, cómo no quedarte editando el capítulo 3 y cómo guardar una versión antes de reescribir.',
    title: 'Cómo cerrar el primer borrador de tu novela',
    publishedAt: '2026-10-30',
    readingMinutes: 7,
    summary:
      'Llegar al final del primer borrador es un hito distinto de publicar o de pulir cada frase. Aquí tienes criterios claros para cerrar esa primera vuelta y pasar a la reescritura con el manuscrito completo.',
    sections: [
      {
        heading: 'Primera vuelta y versión publicable no son lo mismo',
        paragraphs: [
          'Muchos proyectos se estancan en el capítulo intermedio porque cada sesión se convierte en reescritura del inicio. Eso puede mejorar páginas sueltas, pero retrasa el momento en que sabes si la historia entera funciona.',
          'El primer borrador es la versión en la que existe un final —aunque sea provisional— y en la que cada capítulo planificado ya tiene texto, aunque haya huecos marcados como “arreglar después”. Cerrarlo no significa que esté listo para lectores ni para imprenta; significa que dejaste de escribir hacia adelante por primera vez.',
          'Si ya trabajas con constancia y con un mapa de capítulos, el paso siguiente suele ser este: decidir qué falta para poder decir “tengo un borrador completo” y no “llevo años en el primer tercio”.',
        ],
      },
      {
        heading: 'Define antes qué cuenta como “cerrado”',
        paragraphs: [
          'Sin una definición concreta, el borrador se alarga indefinidamente. Escríbela en una nota visible: por ejemplo, “todos los capítulos de la lista tienen al menos un borrador continuo” o “escribí la escena final aunque el epílogo sea un placeholder”.',
          'Incluye explícitamente lo que no forma parte de esta vuelta: perfeccionar diálogos, unificar voz, investigar detalles históricos finos o rediseñar el capítulo 1. Esas tareas pertenecen a la segunda vuelta; mezclarlas ahora suele ser la forma más lenta de no terminar.',
          'Si tu plan cambió al escribir —capítulos nuevos, uno eliminado, un giro que movió el desenlace— actualiza la lista una vez y vuelve a contar qué falta. Cerrar el borrador no es congelar el plan del día uno; es tener un manuscrito entero que refleje la historia que hoy quieres contar.',
        ],
      },
      {
        heading: 'Escribe hacia el final con reglas de emergencia',
        paragraphs: [
          'En la recta final conviene bajar el listón de calidad a cambio de continuidad. Si una transición no te convence, deja un comentario breve en el margen o en una nota (“conectar con cap. 12”) y sigue. Si falta una escena puente, escribe tres frases que indiquen qué debe ocurrir y marca el bloque para expandirlo después.',
          'Resiste volver al capítulo 2 cada vez que dudas del tono. Anota la duda en pendientes y aplícala en bloque cuando el borrador esté completo. Así evitas pulir el inicio mientras el final sigue en blanco.',
          'Cuando llegues al desenlace, escribe hasta el último beat narrativo aunque la emoción no te emocione todavía. Un final imperfecto pero escrito te dice si la arquitectura de la trama aguanta; un final imaginado solo en tu cabeza no.',
        ],
      },
      {
        heading: 'Marca huecos sin convertirlos en excusa para parar',
        paragraphs: [
          'Un borrador cerrado puede contener etiquetas claras: [REVISAR], [DATO], [MOVER ESCENA]. Lo importante es que el lector interno —tú, dentro de un mes— pueda recorrer la novela de principio a fin sin saltos vacíos.',
          'Haz un inventario rápido al terminar: capítulos demasiado cortos, escenas duplicadas, personajes que desaparecen, hilos abiertos. No lo resuelvas en caliente; es la hoja de ruta de la reescritura.',
          'Compara ese inventario con tu organizador de tramas. Si un hilo aparece en notas pero nunca en el texto, decide si entra en la segunda vuelta o si sobra. Cerrar el borrador también limpia expectativas sobre lo que la historia realmente contiene.',
        ],
      },
      {
        heading: 'Guarda la vuelta y celebra el hito',
        paragraphs: [
          'Antes de tocar de nuevo el capítulo 1, guarda una versión con nombre explícito: “Borrador 1 completo — 2026-10-30” o similar. Ese snapshot te protege cuando empieces a cortar escenas y quieras recuperar un párrafo antiguo.',
          'Descansa unos días si puedes. Volver con ojos frescos convierte el inventario de huecos en un plan de segunda vuelta realista, no en una acusación contra tu ritmo.',
          'Si quieres un solo lugar para escribir capítulos, ordenar la trama, anotar pendientes y guardar versiones sin que una inteligencia artificial lea tu manuscrito, puedes empezar en Escribonauta: el plan vigente es gratuito, no pide tarjeta de crédito y está pensado para llevar la novela del mapa al borrador completo con tu texto siempre privado.',
          'Cerrar el primer borrador no es el final del camino, pero es el punto en el que dejas de preguntarte si podrás terminar la historia y empiezas a preguntarte cómo mejorarla. Ese cambio de pregunta merece reconocerse — y merece la siguiente sesión de escritura.',
        ],
      },
    ],
    relatedFeatures: [
      { path: '/explorar/escribir-novelas', label: 'Escribir novelas' },
      { path: '/explorar/versiones', label: 'Versiones' },
      { path: '/blog/constancia-para-terminar-tu-novela', label: 'Constancia para terminar tu novela' },
    ],
  },
]

export function getExploreFeatureBySlug(slug: string): ExploreFeaturePage | undefined {
  return EXPLORE_FEATURES.find((page) => page.slug === slug)
}

export function getBlogArticleBySlug(slug: string): BlogArticle | undefined {
  return BLOG_ARTICLES.find((article) => article.slug === slug)
}

export const ALL_PUBLIC_SEO_PATHS: string[] = [
  '/',
  '/funcionalidades',
  '/preguntas-frecuentes',
  '/proximamente',
  ...EXPLORE_FEATURES.map((page) => page.path),
  '/blog',
  ...BLOG_ARTICLES.map((article) => article.path),
]
